import React from "react";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../store/platform-slice";
import { DemoReportMessage, DemoReportOverlay } from "./DashboardScreen";
import { Outlet, useLocation } from "react-router-dom";
import PageLoader from "../../../common/ui/PageLoader";
import { useAuth } from "../../../providers/AuthProvider";
import { useSubject } from "../providers/SubjectProvider";
import { PlatformResourceStatus } from "../../../providers/AppStore";
import {
  isResourcePending,
  usePlatformResource,
} from "../hooks/usePlatformResource";
import WorkoutPlanProvider from "../components/programming/workoutPlan/store/WorkoutPlanProvider";
import { useWorkoutPlan } from "../components/programming/workoutPlan/store/useWorkoutPlan";

const ProgrammingScreen = () => {
  const { subject, subjectId } = useSubject();
  const { AuthUser } = useAuth();
  const { pathname } = useLocation();
  const reportStatus = usePlatformResource("report", subjectId, AuthUser.role);

  const isNutritionRoute = /\/nutrition\/?$/.test(pathname);
  const isWorkoutRoute = /\/workout\/?$/.test(pathname);

  // The workout plan store lives here, not in its tab: it decides whether the
  // subject has a plan, which the demo fade below needs too, and it keeps its
  // week cache while the user moves between the Programming tabs. It only
  // fetches on the Workout tab. Keyed by subject so switching client drops
  // every cache. It mounts ABOVE the report gate so the probe runs alongside
  // the (slow) report request instead of after it.
  return (
    <WorkoutPlanProvider
      key={subjectId ?? "no-subject"}
      subjectId={subjectId ?? ""}
      role={AuthUser.role}
      enabled={isWorkoutRoute && !!subjectId}
      analysisEnabled={subject?.has_advanced_app_access === true}
    >
      <ProgrammingOutlet
        reportStatus={reportStatus}
        showsRealNutritionPlan={isNutritionRoute && subject?.has_active_plan === true}
        isWorkoutRoute={isWorkoutRoute}
      />
    </WorkoutPlanProvider>
  );
};

export default ProgrammingScreen;

type OutletProps = {
  reportStatus: PlatformResourceStatus;
  showsRealNutritionPlan: boolean;
  isWorkoutRoute: boolean;
};

const ProgrammingOutlet = ({
  reportStatus,
  showsRealNutritionPlan,
  isWorkoutRoute,
}: OutletProps) => {
  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );
  const { planAvailability } = useWorkoutPlan();

  // The report is read by all three tabs except the Workout tab's weekly plan,
  // which comes from the NUT service. So the Workout route waits for the
  // report only once the probe has said there is no plan — that is when it
  // falls back to the report-based `WorkoutPlanTab`. While the probe is still
  // running (or has failed) the tab shows its own skeleton or error banner,
  // neither of which reads the report.
  const needsReport = !isWorkoutRoute || planAvailability === "none";
  if (needsReport && isResourcePending(reportStatus)) {
    return <PageLoader />;
  }

  // The demo fade marks report-derived content as sample data. The Nutrition
  // tab of a subject with an active meal plan, and the Workout tab of a
  // subject with a weekly workout plan, show real plans from the NUT
  // service, not the report, so they are neither sample nor faded — even
  // while the subject's report is still the demo one.
  const showsRealWorkoutPlan = isWorkoutRoute && planAvailability === "available";
  const fadeAsDemo = isDemoReport && !showsRealNutritionPlan && !showsRealWorkoutPlan;

  return (
    <div style={styles.container}>
      {fadeAsDemo && (
        <div style={styles.demoReportWrapper}>
          <DemoReportMessage />
        </div>
      )}
      {fadeAsDemo ? (
        <DemoReportOverlay>
          <Outlet />
        </DemoReportOverlay>
      ) : (
        <Outlet />
      )}
    </div>
  );
};

const styles = {
  container: {
    width: "100%",
  },
  demoReportWrapper: {
    paddingBottom: "16px",
  },
};
